import { useNewTranslation } from 'hooks'

import styled from 'particles/themes/styled'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import AmbassadorTable from './Table'
import { useDispatch, useSelector } from 'react-redux'
import { selectIsInternalUser, selectTeamList } from 'state/profile'
import Text from 'particles/Text'
import { DeniedPage } from 'components/Shared/ErrorPage'
import TagAddModal from 'components/Tag/AddModal'
import TagHistoryModal from 'components/Tag/HistoryModal'
import Filters from './Filters'
import { setSelectedRegistrationView, setSelectedSubmissionView } from 'state/review'
import { selectTeamFilter } from 'state/filter'
import { requestProfileOptions } from 'state/constants'
import { usePermission } from 'hooks/permissions'
import { Ambassador } from 'components/Shared/Permission/enums'
import _ from 'lodash'
import { PLATFORM_CONTENT_TYPE_ENUM } from 'services/api/constants'
import Loading from 'components/Loading'
import { selectRequestsAreLoading } from 'state'
import { STATE_KEYS } from 'state/requests'
import { getAmbassadorBrands, saveAmbassadorBrands, saveAmbassadorList } from 'state/ambassadors'

const TopAmbassador = () => {
    const [t] = useNewTranslation()
    const isInternalUser = useSelector(selectIsInternalUser)
    const dispatch = useDispatch()
    const headerRef = useRef<HTMLDivElement>(null)
    const [tableHeight, setTableHeight] = useState('')
    const hasPermission = usePermission(Ambassador.AddRemoveFavourite)
    const teamFilter = useSelector(selectTeamFilter)
    const teamList = useSelector(selectTeamList)
    const [contentType, setContentType] = useState<PLATFORM_CONTENT_TYPE_ENUM | null>(null)
    const isBrandLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.AmbassadorBrands]))

    useEffect(() => {
        dispatch(
            getAmbassadorBrands({
                team_uuid: teamFilter || undefined,
            }),
        )

        return () => {
            dispatch(saveAmbassadorBrands([]))
        }
    }, [teamFilter])

    const currTeam = useMemo(() => {
        if (!teamFilter) {
            return null
        }
        return teamList.find(team => team.id === teamFilter)
    }, [teamFilter, teamList])

    useEffect(() => {
        dispatch(setSelectedSubmissionView(null))

        dispatch(setSelectedRegistrationView(null))
        dispatch(requestProfileOptions({}))

        return () => {
            dispatch(saveAmbassadorList([]))
        }
    }, [])

    useEffect(() => {
        if (headerRef.current) {
            setTableHeight(`calc(100vh - ${(headerRef.current?.offsetHeight || 0) + 118}px)`) // 118 is accumulation of gap & other spacing
        }
    }, [headerRef.current])

    if (!hasPermission || (!isInternalUser && !currTeam)) {
        return <DeniedPage title={t('emptyState.cantViewPageTitle')} subtitle={t('emptyState.cantViewPageSubtitle')} />
    }

    if (isBrandLoading) {
        return <Loading />
    }

    return (
        <AmbassadorsDashboardContainer>
            <div ref={headerRef} className="page-header">
                <div>
                    <Text scale="H4">
                        {isInternalUser
                            ? t('manageAmbassandor.brandListTitle')
                            : t('manageAmbassandor.pageTitle', {
                                  teamName: currTeam?.label,
                              })}
                    </Text>
                    <Text scale="bodyL500" color="secondary">
                        {t('manageAmbassandor.pageSubtitle')}
                    </Text>
                </div>
                <Filters contentType={contentType} setContentType={setContentType} />
            </div>

            <AmbassadorTable tableHeight={tableHeight} contentType={contentType} />

            <TagAddModal />
            <TagHistoryModal />
        </AmbassadorsDashboardContainer>
    )
}

export default TopAmbassador

const AmbassadorsDashboardContainer = styled.div`
    width: calc(100%-70px);
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px 24px;

    .page-header {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    @media screen and (max-width: 768px) {
        padding: 16px;

        .dropdown-filters {
            flex-direction: column;
            align-items: flex-start;

            & > div {
                width: 100%;
            }
        }
    }
`
